// Token utility helpers.
//
// Auth tokens now live in an httpOnly cookie set by the backend (see
// `utils/authClient.js`). What we keep in localStorage under the `token` key
// is just the marker string `cookie-auth` — a "user is logged in" flag.
// These helpers preserve the original public API used across the app while
// adapting to the new cookie-based world:
//   * `getAuthHeaders()` keeps returning a Bearer header so the existing ~150
//     fetch call sites compile and run unchanged. Backend ignores the marker
//     when a valid cookie is present.
//   * `isTokenExpired()` returns false for the marker (the server is the
//     source of truth on expiry now). Legacy real JWTs are still inspected
//     for backward compat with any cached state from older builds.
//   * `validateTokenOrLogout()` only forces a logout when the marker is
//     missing; expiry is detected by 401 responses (handled centrally in
//     `authClient.js`).

import { COOKIE_AUTH_MARKER, serverLogout } from './authClient';

const PUBLIC_PATHS = [
  '/', '/login', '/register', '/registrieren', '/subscription', '/privacy',
  '/uebersicht',
  '/datenschutz', '/impressum', '/terms', '/agb', '/home-classic',
  '/forgot-password', '/passwort-vergessen', '/reset-password',
  '/passwort-zuruecksetzen', '/verify-email', '/widerruf', '/withdrawal',
  '/widerrufsformular', '/withdrawal-form', '/gewaehrleistung',
  '/gewährleistung', '/warranty', '/partner/bewerben',
  '/affiliate/apply', '/features', '/funktionen',
  '/rechner', '/kennzahlen-rechner',
  '/barrierefreiheit', '/accessibility',
  '/einladung/annehmen', '/accept-invite', '/accept-invitation',
  '/partner-werden', '/partner-login', '/partner',
];

export const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  // Cookie marker — server cookie is the real auth, expiry handled by 401s.
  if (token === COOKIE_AUTH_MARKER) return false;

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  // Consider token expired 5 minutes before actual expiration for safety
  const bufferTime = 5 * 60 * 1000;
  return currentTime > (expirationTime - bufferTime);
};

export const getTokenTimeRemaining = (token) => {
  if (!token || token === COOKIE_AUTH_MARKER) return 0;

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return 0;

  const expirationTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const remaining = expirationTime - currentTime;
  return Math.max(0, Math.floor(remaining / 60000));
};

const isPublicPath = (path) =>
  PUBLIC_PATHS.some((p) => path === p) || path === '/lms' || path.startsWith('/lms/') || (path.startsWith('/kurs/') && path.endsWith('/vorschau')) || path.startsWith('/play/') || path.startsWith('/partner/') || path.startsWith('/karte/') || path === '/blog' || path.startsWith('/blog/') || path === '/unsere-partner' || path === '/shop' || path.startsWith('/shop/') || path.startsWith('/themenwelten/') || path.startsWith('/design/') || path.startsWith('/rechner/') || path.startsWith('/kundenkonto') || path === '/checkout' || path.startsWith('/checkout/') || path.startsWith('/merkzettel') || path === '/anfrage' || path.startsWith('/anfrage/') || path.startsWith('/seite/') || path.startsWith('/p/') || path === '/kategorien' || path === '/categories' || path === '/immobilien' || path.startsWith('/immobilien/') || path.startsWith('/review/');

export const validateTokenOrLogout = () => {
  const token = localStorage.getItem('token');
  const adminToken = localStorage.getItem('admin_token');

  // Cookie marker present → trust it. The fetch interceptor in
  // `authClient.js` will catch any actual 401 and clear state.
  if (token === COOKIE_AUTH_MARKER) return true;

  // Admin session is preserved separately via `admin_token`/`admin_user` and
  // does NOT rely on the legacy JWT cookie marker. Never wipe those.
  if (adminToken) return true;

  // Okt 2026 · Anonyme Besucher (gar kein Token) NICHT auf /login schicken —
  // unbekannte URLs landeten sonst auf der Login-Seite (Google: Soft 404).
  if (!token) return false;

  // Stale legacy JWT → log out.
  if (isTokenExpired(token)) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Do NOT remove admin_token/admin_user here — they are managed by the
    // AdminApp login/logout flow and must survive full page reloads.

    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/admin')) return false;
    if (!isPublicPath(currentPath)) {
      window.location.href = '/login?expired=true';
    }
    return false;
  }
  return true;
};

export const setupTokenValidation = () => {
  validateTokenOrLogout();

  const intervalId = setInterval(() => {
    validateTokenOrLogout();
  }, 5 * 60 * 1000);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      validateTokenOrLogout();
    }
  });

  return intervalId;
};

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  if (!token || isTokenExpired(token)) {
    validateTokenOrLogout();
    return null;
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// Convenience helpers for callers that want to perform a clean logout.
export const performLogout = async () => {
  await serverLogout();
};
